import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useChildren } from "@/hooks/useChildren";
import { useEntitlements } from "@/hooks/useEntitlements";
import {
  useCollectionTemplates,
  type CollectionTemplate,
  type CollectionTemplateStepRow,
} from "@/hooks/useCollectionTemplates";
import { useCollections, type Collection } from "@/hooks/useCollections";
import ChildAvatar from "@/components/ChildAvatar";
import LockedButton from "@/components/LockedButton";
import LockedFeatureSheet, { type FeatureKey } from "@/components/LockedFeatureSheet";
import ParticipantPicker from "@/components/ParticipantPicker";
import type { ParticipantRef } from "@/types/participant";

interface CollectionCreatedInfo {
  id: string;
  name: string;
  icon: string;
  color: string;
  collectionMode: "guided" | "open" | "hybrid";
  templateId: string | null;
  lockedChildIds: string[];
  lockedMemberIds: string[];
  participantChildIds: string[];
  participantMemberIds: string[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  existingCollections: Collection[];
  onStarted: () => void;
  /** Called after a collection is created with the new collection's data.
   *  Use this to navigate directly into the collection's detail view. */
  onCollectionCreated?: (info: CollectionCreatedInfo) => void;
  /** When true, jumps straight into the custom-creation form instead of
   *  showing the template grid. Caller is responsible for entitlement checks. */
  startInCustomFlow?: boolean;
}

const EMOJI_OPTIONS = [
  "🌟", "⭐", "🔥", "❤️", "💫", "🎉", "🎂", "🎈",
  "👶", "🧒", "👦", "👧", "🐣", "🦋",
  "🎨", "🎵", "📚", "⚽", "🏖️", "🌍", "🚗", "🏠",
  "🌸", "🍂", "🌈", "🐾", "📸", "🎯", "🌙", "✨",
];

const COLOR_OPTIONS = [
  "#F2A623",
  "#D85A30",
  "#1D9E75",
  "#534AB7",
  "#E8748A",
  "#2BA3A3",
  "#0C447C",
  "#8B5E3C",
];

// ── Template wizard step type ────────────────────────────────────────────────
type TemplateFlowStep = "preview" | "subject" | "name";

// ── Custom collection creation step type ────────────────────────────────────
type CustomStep = "name" | "subject";

export default function CollectionTemplateBrowser({
  isOpen,
  onClose,
  existingCollections,
  onStarted,
  onCollectionCreated,
  startInCustomFlow = false,
}: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { children } = useChildren();
  const { canCustomCollections, canActiveCapture } = useEntitlements();
  const { templates, isLoading: loading, startCollectionFromTemplate, createCustomCollection, fetchTemplateSteps } = useCollectionTemplates();
  const { startActiveMode } = useCollections();
  // Locked sheet shown when the user taps a premium template tile they
  // can't access — single state is fine since only one sheet opens at a time.
  const [lockedSheet, setLockedSheet] = useState<FeatureKey | null>(null);

  // ── Template wizard state ─────────────────────────────────────────────────
  const [flowTemplate, setFlowTemplate] = useState<CollectionTemplate | null>(null);
  const [templateFlowStep, setTemplateFlowStep] = useState<TemplateFlowStep | null>(null);
  const [templateSteps, setTemplateSteps] = useState<CollectionTemplateStepRow[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateSelectedChildIds, setTemplateSelectedChildIds] = useState<string[]>([]);
  const [subjectParticipants, setSubjectParticipants] = useState<ParticipantRef[]>([]);
  const [templateSaving, setTemplateSaving] = useState(false);
  // Default off when the user can't use active-capture — avoids promising
  // something we won't honour below when startActiveMode is skipped.
  const [templateHoardNow, setTemplateHoardNow] = useState(canActiveCapture);

  // ── Custom collection state ───────────────────────────────────────────────
  const [customStep, setCustomStep] = useState<CustomStep | null>(null);
  const [customName, setCustomName] = useState("");
  const [customIcon, setCustomIcon] = useState("🌟");
  const [customColor, setCustomColor] = useState("#F2A623");
  const [customChildIds, setCustomChildIds] = useState<string[]>([]);
  const [customParticipants, setCustomParticipants] = useState<ParticipantRef[]>([]);
  const [customSaving, setCustomSaving] = useState(false);
  const [customHoardNow, setCustomHoardNow] = useState(canActiveCapture);

  // When caller requests direct-to-custom, skip the template grid and land
  // on the name/icon/color step. Runs each time the sheet opens; handleClose
  // resets customStep back to null so reopens re-trigger the jump.
  useEffect(() => {
    if (isOpen && startInCustomFlow) {
      setCustomStep((prev) => (prev ? prev : "name"));
    }
  }, [isOpen, startInCustomFlow]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  // Family-level templates (one_per_child = false): any existing collection
  // from the same template marks the tile as started → tile disabled.
  // Per-child templates (one_per_child = true): the tile stays enabled as
  // long as at least one child is not yet covered. It's only marked started
  // once every child has a collection from that template.
  const existingByTemplate = new Map<string, Collection[]>();
  for (const c of existingCollections) {
    if (!c.template_id) continue;
    const list = existingByTemplate.get(c.template_id);
    if (list) list.push(c);
    else existingByTemplate.set(c.template_id, [c]);
  }

  const coveredChildIdsFor = (templateId: string): Set<string> => {
    const set = new Set<string>();
    for (const c of existingByTemplate.get(templateId) ?? []) {
      for (const id of c.locked_child_ids ?? []) set.add(id);
      for (const id of c.participant_child_ids ?? []) set.add(id);
    }
    return set;
  };

  const isTemplateStarted = (tpl: CollectionTemplate): boolean => {
    const rows = existingByTemplate.get(tpl.id);
    if (!rows || rows.length === 0) return false;
    if (!tpl.one_per_child) return true;
    if (children.length === 0) return true;
    const covered = coveredChildIdsFor(tpl.id);
    return children.every((child) => covered.has(child.id));
  };

  const resetTemplateFlow = () => {
    setFlowTemplate(null);
    setTemplateFlowStep(null);
    setTemplateSteps([]);
    setTemplateSelectedChildIds([]);
    setSubjectParticipants([]);
    setTemplateName("");
    setTemplateSaving(false);
    setStepsLoading(false);
    setTemplateHoardNow(canActiveCapture);
  };

  const resetCustomForm = () => {
    setCustomStep(null);
    setCustomName("");
    setCustomIcon("🌟");
    setCustomColor("#F2A623");
    setCustomChildIds([]);
    setCustomParticipants([]);
    setCustomSaving(false);
    setCustomHoardNow(canActiveCapture);
  };

  const handleClose = () => {
    resetTemplateFlow();
    resetCustomForm();
    onClose();
  };

  const handleBack = () => {
    // Custom flow
    if (customStep === "subject") { setCustomStep("name"); return; }
    if (customStep === "name") {
      // Entered directly via startInCustomFlow → back closes the sheet
      // rather than revealing the template grid the user never opened.
      if (startInCustomFlow) { handleClose(); return; }
      resetCustomForm();
      return;
    }

    // Template flow
    if (templateFlowStep === "name") {
      if (flowTemplate?.locks_to_members) {
        // No subject step existed; go back to preview (guided/hybrid) or browser
        if (flowTemplate.mode !== "open") {
          setTemplateFlowStep("preview");
        } else {
          resetTemplateFlow();
        }
      } else {
        setTemplateFlowStep("subject");
      }
      return;
    }
    if (templateFlowStep === "subject") {
      if (flowTemplate && flowTemplate.mode !== "open") {
        setTemplateFlowStep("preview");
      } else {
        resetTemplateFlow();
      }
      return;
    }
    if (templateFlowStep === "preview") { resetTemplateFlow(); return; }
  };

  // ── Template tap ──────────────────────────────────────────────────────────

  const handleTemplateTap = async (template: CollectionTemplate) => {
    if (isTemplateStarted(template)) return;
    // Seedling only sees `premium=false` templates in the grid, but feed
    // cards or deep links could still surface a premium template — belt +
    // braces gate here so tapping one opens the paywall instead of creating.
    if (template.premium && !canCustomCollections) {
      setLockedSheet("premium_template");
      return;
    }
    setFlowTemplate(template);
    setTemplateName(template.name);
    // For per-child templates with at least one existing collection, pre-select
    // the first uncovered child so the user doesn't have to figure out which
    // one the new collection is for. For first-time starts and family-level
    // templates, leave the picker empty (current behaviour).
    let initialChildIds: string[] = [];
    if (template.one_per_child && existingByTemplate.has(template.id)) {
      const covered = coveredChildIdsFor(template.id);
      const firstUncovered = children.find((child) => !covered.has(child.id));
      if (firstUncovered) initialChildIds = [firstUncovered.id];
    }
    setTemplateSelectedChildIds(initialChildIds);
    setSubjectParticipants([]);

    if (template.mode !== "open") {
      // guided or hybrid — fetch steps, show preview
      setStepsLoading(true);
      setTemplateFlowStep("preview");
      const steps = await fetchTemplateSteps(template.id);
      setTemplateSteps(steps);
      setStepsLoading(false);
    } else if (template.locks_to_members) {
      // open + locks_to_members: skip subject, go straight to name
      setTemplateFlowStep("name");
      setTemplateSteps([]);
    } else {
      setTemplateFlowStep("subject");
      setTemplateSteps([]);
    }
  };

  const advanceFromPreview = () => {
    if (!flowTemplate) return;
    if (flowTemplate.locks_to_members) {
      setTemplateFlowStep("name");
    } else {
      setTemplateFlowStep("subject");
    }
  };

  const advanceFromSubject = () => setTemplateFlowStep("name");

  const subjectContinueEnabled = flowTemplate
    ? !flowTemplate.locks_to_children || templateSelectedChildIds.length > 0
    : false;

  // ── Template INSERT ───────────────────────────────────────────────────────

  const handleCreateFromTemplate = async () => {
    if (!flowTemplate || !user || !templateName.trim()) return;
    setTemplateSaving(true);
    try {
      const lockedChildIds = flowTemplate.locks_to_children ? templateSelectedChildIds : [];
      const lockedMemberIds = flowTemplate.locks_to_members ? [user.id] : [];

      const participantMemberIds = flowTemplate.locks_to_members
        ? [user.id]
        : subjectParticipants
            .filter((p): p is Extract<ParticipantRef, { kind: "connection" }> => p.kind === "connection")
            .map((p) => p.connectionId);

      const participantChildIds = templateSelectedChildIds;

      const collectionId = await startCollectionFromTemplate(flowTemplate, user.id, {
        collectionName: templateName.trim(),
        lockedChildIds,
        lockedMemberIds,
        participantChildIds,
        participantMemberIds,
      });

      // Enable hoarding immediately if the toggle is on AND the user has the
      // active-capture entitlement. Non-fatal — if this fails the collection
      // still exists and the user can toggle it on manually from the detail
      // sheet. startActiveMode's clear-stale step handles any previously-active
      // collection automatically. Seedling never reaches this branch because
      // the toggle is locked off.
      if (templateHoardNow && canActiveCapture) {
        try {
          await startActiveMode(collectionId);
        } catch (err) {
          console.warn("[CollectionTemplateBrowser] startActiveMode after create failed:", err);
        }
      }

      onStarted();
      onCollectionCreated?.({
        id: collectionId,
        name: templateName.trim(),
        icon: flowTemplate.icon,
        color: flowTemplate.color,
        collectionMode: flowTemplate.mode,
        templateId: flowTemplate.id,
        lockedChildIds,
        lockedMemberIds,
        participantChildIds,
        participantMemberIds,
      });
      resetTemplateFlow();
      handleClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("toast.collectionCreateError"));
    } finally {
      setTemplateSaving(false);
    }
  };

  // ── Custom INSERT ─────────────────────────────────────────────────────────

  const handleCreateCustom = async () => {
    if (!customName.trim() || !user) return;
    setCustomSaving(true);
    try {
      const participantMemberIds = customParticipants
        .filter((p): p is Extract<ParticipantRef, { kind: "connection" }> => p.kind === "connection")
        .map((p) => p.connectionId);

      const collectionId = await createCustomCollection(user.id, {
        name: customName.trim(),
        icon: customIcon,
        color: customColor,
        participantChildIds: customChildIds,
        participantMemberIds,
      });

      // Enable hoarding immediately if the toggle is on AND entitled — see
      // template flow above for rationale. Non-fatal; collection still exists
      // on failure.
      if (customHoardNow && canActiveCapture) {
        try {
          await startActiveMode(collectionId);
        } catch (err) {
          console.warn("[CollectionTemplateBrowser] startActiveMode after create failed:", err);
        }
      }

      onStarted();
      onCollectionCreated?.({
        id: collectionId,
        name: customName.trim(),
        icon: customIcon,
        color: customColor,
        collectionMode: "open",
        templateId: null,
        lockedChildIds: [],
        lockedMemberIds: [],
        participantChildIds: customChildIds,
        participantMemberIds,
      });
      resetCustomForm();
      handleClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("toast.collectionCreateError"));
      setCustomSaving(false);
    }
  };

  // ── Toggle child selection helpers ────────────────────────────────────────

  const toggleTemplateChild = (childId: string) => {
    setTemplateSelectedChildIds((prev) =>
      prev.includes(childId) ? prev.filter((id) => id !== childId) : [...prev, childId]
    );
  };

  const toggleCustomChild = (childId: string) => {
    setCustomChildIds((prev) =>
      prev.includes(childId) ? prev.filter((id) => id !== childId) : [...prev, childId]
    );
  };

  // ── Derived: which "screen" to show ──────────────────────────────────────

  const showBrowser = !flowTemplate && !customStep;
  const showTemplateFlow = !!flowTemplate && !!templateFlowStep;
  const showCustomFlow = !!customStep;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Sheet open={isOpen} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <SheetContent
        side="bottom"
        className="z-[60] rounded-t-3xl h-[85vh] overflow-y-auto"
        overlayClassName="z-[60]"
        hideCloseButton
      >
        <SheetTitle className="sr-only">
          {showCustomFlow ? "Create a collection" : showTemplateFlow ? (flowTemplate?.name ?? "Collection") : "Explore collections"}
        </SheetTitle>

        {/* ── Shared back/close header (shown when not on the browser grid) ── */}
        {(showTemplateFlow || showCustomFlow) && (
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={handleBack}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            TEMPLATE BROWSER
        ════════════════════════════════════════════════════════════════════ */}
        {showBrowser && (
          <>
            <div className="flex items-center justify-between mb-1">
              <div>
                <p className="text-base font-medium text-foreground">Start a collection</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Pick one and we'll bring you the right cards
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 gap-3 pt-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-36 rounded-xl bg-muted animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 pt-4">
                {/* Create your own — gated by canCustomCollections. Seedling
                    sees the dimmed locked tile that opens the upgrade sheet. */}
                {canCustomCollections ? (
                  <button
                    type="button"
                    onClick={() => setCustomStep("name")}
                    className="text-left rounded-xl p-4 transition-transform active:scale-[0.97]"
                    style={{
                      backgroundColor: "rgba(120,100,80,0.06)",
                      border: "1.5px dashed rgba(120,100,80,0.3)",
                    }}
                  >
                    <span style={{ fontSize: 28 }}>✨</span>
                    <p className="text-[14px] font-medium text-foreground mt-2 leading-tight">
                      {t("collections.custom.tileTitle")}
                    </p>
                    <p
                      className="text-[12px] text-muted-foreground mt-1 leading-snug"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {t("collections.custom.tileSubtext")}
                    </p>
                  </button>
                ) : (
                  <LockedButton locked feature="custom_collections" className="block">
                    <div
                      className="text-left rounded-xl p-4"
                      style={{
                        backgroundColor: "rgba(120,100,80,0.06)",
                        border: "1.5px dashed rgba(120,100,80,0.3)",
                      }}
                    >
                      <span style={{ fontSize: 28 }}>✨</span>
                      <p className="text-[14px] font-medium text-foreground mt-2 leading-tight">
                        {t("collections.custom.tileTitle")}
                      </p>
                      <p
                        className="text-[12px] text-muted-foreground mt-1 leading-snug"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {t("collections.custom.tileSubtext")}
                      </p>
                    </div>
                  </LockedButton>
                )}

                {templates.map((tpl) => {
                  const isStarted = isTemplateStarted(tpl);
                  // Premium templates are locked for users without the entitlement.
                  // `canCustomCollections` tracks the same Seedling→Keeper flip
                  // as premium access, so we reuse it rather than plumbing a
                  // separate `canPremiumTemplates` through the hook.
                  const isLockedPremium = tpl.premium && !canCustomCollections;
                  if (isLockedPremium) {
                    return (
                      <LockedButton key={tpl.id} locked feature="premium_template" className="block">
                        <div
                          className="text-left rounded-xl p-4"
                          style={{
                            backgroundColor: `${tpl.color}14`,
                            border: `1px solid ${tpl.color}40`,
                          }}
                        >
                          <span style={{ fontSize: 28 }}>{tpl.icon}</span>
                          <p className="text-[14px] font-medium text-foreground mt-2 leading-tight">
                            {tpl.name}
                          </p>
                          <p
                            className="text-[12px] text-muted-foreground mt-1 leading-snug"
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {tpl.description}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-2">{t("collections.browser.momentCount", { count: tpl.momentCount })}</p>
                        </div>
                      </LockedButton>
                    );
                  }
                  return (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => !isStarted && void handleTemplateTap(tpl)}
                      disabled={isStarted}
                      className="text-left rounded-xl p-4 transition-transform active:scale-[0.97]"
                      style={{
                        backgroundColor: `${tpl.color}14`,
                        border: `1px solid ${tpl.color}40`,
                        opacity: isStarted ? 0.6 : 1,
                      }}
                    >
                      <span style={{ fontSize: 28 }}>{tpl.icon}</span>
                      <p className="text-[14px] font-medium text-foreground mt-2 leading-tight">
                        {tpl.name}
                      </p>
                      <p
                        className="text-[12px] text-muted-foreground mt-1 leading-snug"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {tpl.description}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-2">
                        {isStarted ? "✓ Started" : t("collections.browser.momentCount", { count: tpl.momentCount })}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            TEMPLATE FLOW — Step A: Step preview (guided / hybrid only)
        ════════════════════════════════════════════════════════════════════ */}
        {showTemplateFlow && templateFlowStep === "preview" && flowTemplate && (
          <div className="space-y-5">
            {/* Template identity */}
            <div className="text-center space-y-1.5">
              <span style={{ fontSize: 40 }}>{flowTemplate.icon}</span>
              <p className="text-lg font-semibold text-foreground">{flowTemplate.name}</p>
              {flowTemplate.description && (
                <p className="text-sm text-muted-foreground">{flowTemplate.description}</p>
              )}
            </div>

            {/* Steps list */}
            <div
              className="rounded-2xl p-4 space-y-3"
              style={{ backgroundColor: `${flowTemplate.color}10`, border: `1px solid ${flowTemplate.color}30` }}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("collections.create.stepPreview.heading")}
              </p>

              {stepsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-5 rounded bg-muted animate-pulse" />
                  ))}
                </div>
              ) : (
                <ul className="space-y-2">
                  {templateSteps.map((step) => (
                    <li key={step.id} className="flex items-start gap-2 text-sm text-foreground">
                      <span className="shrink-0 mt-px" style={{ color: flowTemplate.color }}>·</span>
                      <span>
                        {step.step_title_override ?? step.card?.title ?? "—"}
                        {!step.required && (
                          <span className="text-muted-foreground text-xs ml-1.5">
                            ({t("collections.create.stepPreview.optional")})
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                  {templateSteps.length === 0 && !stepsLoading && (
                    <li className="text-sm text-muted-foreground">No milestones configured yet</li>
                  )}
                </ul>
              )}

              {flowTemplate.mode === "hybrid" && (
                <p className="text-xs text-muted-foreground pt-1 border-t border-border/30">
                  {t("collections.create.stepPreview.hybrid")}
                </p>
              )}
            </div>

            <Button
              className="w-full"
              onClick={advanceFromPreview}
              style={{ backgroundColor: flowTemplate.color }}
            >
              {t("collections.create.stepPreview.cta")}
            </Button>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            TEMPLATE FLOW — Step B: Subject picker
        ════════════════════════════════════════════════════════════════════ */}
        {showTemplateFlow && templateFlowStep === "subject" && flowTemplate && (
          <div className="space-y-5">
            <div>
              <p className="text-base font-semibold text-foreground">
                {t("collections.create.subject.heading")}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {flowTemplate.locks_to_children
                  ? t("collections.create.subject.locked.subtext")
                  : t("collections.create.subject.optional.subtext")}
              </p>
            </div>

            {/* Children picker */}
            {children.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {children.map((child) => {
                  const selected = templateSelectedChildIds.includes(child.id);
                  return (
                    <button
                      key={child.id}
                      type="button"
                      onClick={() => toggleTemplateChild(child.id)}
                      className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition-all"
                      style={
                        selected
                          ? { backgroundColor: flowTemplate.color, color: "#fff" }
                          : { backgroundColor: "var(--muted)", color: "var(--muted-foreground)" }
                      }
                    >
                      <ChildAvatar child={child} size={24} />
                      <span className="font-medium">{child.name}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Adult member picker — Variant 3 only (neither locked) */}
            {!flowTemplate.locks_to_children && (
              <div className="space-y-2">
                <ParticipantPicker
                  mode="collection"
                  value={subjectParticipants}
                  onChange={setSubjectParticipants}
                  accentColor={flowTemplate.color}
                />
              </div>
            )}

            {/* Variant 1 (locks_to_children): only [Continue →], disabled until ≥1 child */}
            {/* Variant 3 (neither): [Skip] + [Continue →] */}
            <div className="flex gap-2 pt-2">
              {!flowTemplate.locks_to_children && (
                <Button variant="outline" className="flex-1" onClick={advanceFromSubject}>
                  Skip
                </Button>
              )}
              <Button
                className="flex-1"
                onClick={advanceFromSubject}
                disabled={!subjectContinueEnabled}
                style={{ backgroundColor: flowTemplate.color }}
              >
                Continue →
              </Button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            TEMPLATE FLOW — Step C: Collection name
        ════════════════════════════════════════════════════════════════════ */}
        {showTemplateFlow && templateFlowStep === "name" && flowTemplate && (
          <div className="space-y-5">
            <div>
              <p className="text-base font-semibold text-foreground">
                {t("collections.create.name.label")}
              </p>
            </div>

            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              maxLength={60}
              autoFocus
              className="w-full rounded-xl border border-border/60 bg-muted/30 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />

            {/* Hoard-now toggle: restores the old "Start collection" ergonomic
                where creating + hoarding happened in one gesture. Locked for
                tiers without active-capture — tapping opens the paywall and
                the real control stays unchecked. */}
            {canActiveCapture ? (
              <label className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/20 px-4 py-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={templateHoardNow}
                  onChange={(e) => setTemplateHoardNow(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded accent-primary"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground leading-tight">
                    {t("collections.create.hoardNow.label")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("collections.create.hoardNow.subtext")}
                  </p>
                </div>
              </label>
            ) : (
              <LockedButton locked feature="active_capture" className="block">
                <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={false}
                    readOnly
                    className="mt-0.5 h-4 w-4 shrink-0 rounded accent-primary"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground leading-tight">
                      {t("collections.create.hoardNow.label")}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("collections.create.hoardNow.subtext")}
                    </p>
                  </div>
                </div>
              </LockedButton>
            )}

            <Button
              className="w-full"
              onClick={() => void handleCreateFromTemplate()}
              disabled={!templateName.trim() || templateSaving}
              style={{ backgroundColor: flowTemplate.color }}
            >
              {templateSaving ? "Creating..." : t("collections.create.cta")}
            </Button>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            CUSTOM FLOW — Step A: Name + icon + color
        ════════════════════════════════════════════════════════════════════ */}
        {showCustomFlow && customStep === "name" && (
          <div className="space-y-5">
            {/* Live preview */}
            <div
              className="flex items-center gap-3 rounded-xl p-3"
              style={{ backgroundColor: `${customColor}14`, border: `1px solid ${customColor}40` }}
            >
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: `${customColor}20` }}
              >
                <span style={{ fontSize: 26 }}>{customIcon}</span>
              </div>
              <div>
                <p className="text-[15px] font-medium text-foreground leading-tight">
                  {customName.trim() || "Your collection"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">0 entries</p>
              </div>
            </div>

            {/* Name */}
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t("collections.custom.name.label")}
              </p>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder={t("collections.custom.name.placeholder")}
                autoFocus
                maxLength={60}
                className="w-full rounded-xl border border-border/60 bg-muted/30 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            {/* Emoji picker */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Icon</p>
              <div className="flex flex-wrap gap-1.5">
                {EMOJI_OPTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setCustomIcon(emoji)}
                    className="flex h-9 w-9 items-center justify-center rounded-xl transition-all"
                    style={{
                      fontSize: 20,
                      backgroundColor: customIcon === emoji ? `${customColor}22` : "transparent",
                      border: customIcon === emoji
                        ? `1.5px solid ${customColor}`
                        : "1.5px solid transparent",
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            {/* Color picker */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Color</p>
              <div className="flex gap-3">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setCustomColor(color)}
                    className="h-8 w-8 rounded-full transition-transform hover:scale-110 active:scale-95"
                    style={{
                      backgroundColor: color,
                      boxShadow: customColor === color
                        ? `0 0 0 2px white, 0 0 0 3.5px ${color}`
                        : "none",
                    }}
                  />
                ))}
              </div>
            </div>

            <Button
              onClick={() => setCustomStep("subject")}
              disabled={!customName.trim()}
              className="w-full"
              style={{ backgroundColor: customColor }}
            >
              Continue →
            </Button>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            CUSTOM FLOW — Step B: Subject picker (optional)
        ════════════════════════════════════════════════════════════════════ */}
        {showCustomFlow && customStep === "subject" && (
          <div className="space-y-5">
            <div>
              <p className="text-base font-semibold text-foreground">
                {t("collections.create.subject.heading")}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t("collections.create.subject.optional.subtext")}
              </p>
            </div>

            {/* Children picker (optional) */}
            {children.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {children.map((child) => {
                  const selected = customChildIds.includes(child.id);
                  return (
                    <button
                      key={child.id}
                      type="button"
                      onClick={() => toggleCustomChild(child.id)}
                      className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition-all"
                      style={
                        selected
                          ? { backgroundColor: customColor, color: "#fff" }
                          : { backgroundColor: "var(--muted)", color: "var(--muted-foreground)" }
                      }
                    >
                      <ChildAvatar child={child} size={24} />
                      <span className="font-medium">{child.name}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Adult member picker (optional) */}
            <ParticipantPicker
              mode="collection"
              value={customParticipants}
              onChange={setCustomParticipants}
              accentColor={customColor}
            />
            <p className="text-[11px] text-muted-foreground -mt-3">
              Leave empty to tag anyone. Otherwise only these people will
              show up when adding memories under this collection.
            </p>

            {/* Hoard-now toggle: locked on Seedling (matches template flow). */}
            {canActiveCapture ? (
              <label className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/20 px-4 py-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={customHoardNow}
                  onChange={(e) => setCustomHoardNow(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded accent-primary"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground leading-tight">
                    {t("collections.create.hoardNow.label")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("collections.create.hoardNow.subtext")}
                  </p>
                </div>
              </label>
            ) : (
              <LockedButton locked feature="active_capture" className="block">
                <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={false}
                    readOnly
                    className="mt-0.5 h-4 w-4 shrink-0 rounded accent-primary"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground leading-tight">
                      {t("collections.create.hoardNow.label")}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("collections.create.hoardNow.subtext")}
                    </p>
                  </div>
                </div>
              </LockedButton>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => void handleCreateCustom()}
                disabled={customSaving}
              >
                Skip
              </Button>
              <Button
                className="flex-1"
                onClick={() => void handleCreateCustom()}
                disabled={customSaving}
                style={{ backgroundColor: customColor }}
              >
                {customSaving ? "Creating..." : t("collections.create.cta")}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>

      {lockedSheet && (
        <LockedFeatureSheet
          feature={lockedSheet}
          open={!!lockedSheet}
          onClose={() => setLockedSheet(null)}
        />
      )}
    </Sheet>
  );
}
